import { Inbox } from 'lucide-react';

import { cn } from '@shared/lib';

import { t } from '@/i18n';

import type { I18nKey } from '@/i18n';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * 결과가 0건일 때의 빈 상태 표시 (TRAD-2107 · API 확장 TRAD-2106).
 *
 * **기준 컴포넌트**: 같은 자리(요청 결과 없음/실패)를 담당하는 `shared/ui/ErrorState`.
 * 레이아웃 골격(중앙정렬 · 아이콘 · 문구 · size sm/md 스케일)을 **그대로** 맞춰, 두 상태가
 * 같은 자리에서 자리이동 없이 교체되게 한다.
 *
 * 🔴 **장애와 빈 상태는 반드시 "보기에도" 달라야 한다.** 그래서 ErrorState 와 의도적으로
 * 다르게 만든 것이 셋 있다:
 *   ① `role="status"` (ErrorState 는 `role="alert"`) — 스크린리더에게도 "경고 아님"
 *   ② 아이콘이 `Inbox` (ErrorState 는 `AlertTriangle`)
 *   ③ **재시도 버튼이 없다** — 0건은 다시 불러도 0건이다. 재시도를 주면 화면이
 *      "실패했으니 다시 해보라"고 거짓말을 한다.
 * 이 구분이 없어서 실제 사고가 났다: android 에서 네트워크 완전 차단 상태의 예측 마켓
 * 목록이 "마켓 없음, 다른 필터를 써보세요"로 떠서, 온라인 0건 화면과 픽셀 단위로 같았다
 * (= 사용자가 장애를 인지할 방법이 없었다).
 *
 * 🔴 `action` 은 위 ③의 예외가 **아니다.** "필터 초기화"처럼 **0건이라는 사실을 바꾸는
 * 행동**만 넣는다. 재시도(refetch) 버튼은 절대 넣지 말 것 — 같은 요청을 다시 보내는 버튼이
 * 붙는 순간 이 컴포넌트는 ErrorState 와 구분되지 않는다.
 *
 * ⚠️ 문구는 하드코딩 금지. 기본은 카탈로그 키(`titleKey`/`descriptionKey`)로 받고,
 *   `title` 은 **소비자가 이미 `t()` 로 지역화한 문자열**을 넘기는 경로 전용이다
 *   (`widgets/list-view` 의 `emptyMessage` 처럼 prop 시그니처가 `string` 인 경우).
 *   `title` 에 리터럴을 직접 넣으면 EN 로케일에 한글이 노출된다.
 *   `titleKey`·`title` 이 둘 다 없으면 공용 `list.empty.default` 로 폴백한다.
 *
 * ⚠️ 아이콘은 **lucide**. 이모지를 아이콘으로 쓰지 않는다(감사 SoT-14 / W-06 · TRAD-2106).
 *   TRAD-2106 에서 `features/financial-calendar/ui/EventList.tsx`(`📅`)와
 *   `pages/polymarket/LeaderboardPage.tsx` 의 로컬 사본을 이 컴포넌트로 이관하며 정리했다.
 */
interface EmptyStateProps {
  /** 표시할 문구의 카탈로그 키. */
  titleKey?: I18nKey;
  /** 이미 `t()` 로 지역화된 문자열. 🔴 하드코딩 금지 — 반드시 `t()` 결과만 넘길 것. */
  title?: string;
  /** 보조 설명의 카탈로그 키(선택). */
  descriptionKey?: I18nKey;
  /** 아이콘 컴포넌트. 기본 `Inbox`. 🔴 lucide 만 — 이모지 금지. */
  icon?: LucideIcon;
  /** 행동 유도(예: 필터 초기화 버튼). 없으면 렌더하지 않는다. 🔴 재시도 버튼 금지. */
  action?: ReactNode;
  /** `sm` = 카드·위젯 안(기본), `md` = 리스트 전체 영역 */
  size?: 'sm' | 'md';
  className?: string;
}

export const EmptyState = ({
  titleKey,
  title,
  descriptionKey,
  icon: Icon = Inbox,
  action,
  size = 'sm',
  className,
}: EmptyStateProps) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center gap-3',
      size === 'md' ? 'py-20 gap-4' : 'py-8',
      className,
    )}
    role="status"
  >
    <Icon className={size === 'md' ? 'h-10 w-10' : 'h-6 w-6'} aria-hidden="true" />
    <div className="flex flex-col gap-1.5">
      <p className="text-muted-foreground text-sm">{title ?? t(titleKey ?? 'list.empty.default')}</p>
      {descriptionKey && <p className="text-muted-foreground text-xs">{t(descriptionKey)}</p>}
    </div>
    {action}
  </div>
);
